import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
// eslint-disable-next-line import/no-cycle
import { Icon } from "root/libs/ui-components/src";
import DoubleChevronDown from "icons/cx/iconDoubleChevron.svg";
import {
  isEnter,
  isSpaceBar,
} from "one-time-shipment/src/helpers/spaceBarEvents";
import AccordionContent from "./AccordionContent";

// # region Component Styles
const IconWrapper = styled(Icon)`
  ${(props) =>
    props.isExpanded
      ? css`
          transform: rotate(180deg);
        `
      : undefined}
  display: inline-flex;
  height: calc(1.68rem);
  width: calc(1.68rem);
  margin-left: var(--dui-size-space-3x);
`;

const Container = styled.div`
  display: flex;
  position: relative;
`;

const StyledButton = styled.button.attrs((props) => ({
  id: props.id,
  tabIndex: props.tabIndex,
  onKeyDown: props.onKeyDown,
}))`
  display: inline-flex;
  align-items: center;
  background-color: var(--dui-color-white-500);
  border-radius: var(--dui-size-radius-md);
  font-size: var(--dui-size-font-md);
  color: var(--dui-color-red-500);
  font-weight: 700;
  padding: var(--dui-size-space-5x)
    calc(var(--dui-size-space-10x) + var(--dui-size-space-5x));
  border: 0 !important;
  line-height: var(--dui-size-line-height-2xl);
  z-index: 3;

  &:hover {
    color: var(--dui-color-red-400) !important;
  }
`;

const AccordionTag = styled.div`
  display: flex;
  flex-direction: column;
`;

const ExpandCollapseButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  ${(props) =>
    props.isExpanded
      ? css`
          order: 2;
        `
      : undefined}
  .expandCollapseButton {
    cursor: pointer;
  }

  @media print {
    display: none;
  }
`;
// # endregion

const AccordionTrigger = (props) => {
  const reference = useRef(null);
  const { focusedIndex, childIndex } = props;

  useEffect(() => {
    if (focusedIndex === childIndex && reference.current) {
      reference.current.focus();
    }
  }, [focusedIndex]);
};

const Accordion = ({
  id,
  className,
  dataTestid,
  children,
  activeIndex: activeIndexProp,
  focusedIndex: focusedIndexProp,
  onTitleClick: onTitleClickProp,
  expandButtonLabel,
  collapseButtonLabel,
  expandButtonAriaLabel,
  collapseButtonAriaLabel,
  closeAlsoOnContentClick,
  expandCollapseButtonTestId,
  expandButtonAriaControls,
  expandButtonDataTracking,
  onKeyDown,
}) => {
  const [activeIndex, setActiveIndex] = useState(activeIndexProp);
  const [focusedIndex, setFocusedIndex] = useState(focusedIndexProp);

  const isIndexExpanded = (index) => index === activeIndex;

  const onTitleClick = (event, titleProps) => {
    const { index, isExpanded } = titleProps;
    const wasThisTitleClicked = isIndexExpanded(index);
    const newActiveIndex = wasThisTitleClicked && isExpanded ? -1 : index;
    setActiveIndex(newActiveIndex);
    onTitleClickProp(titleProps, isExpanded);
  };

  const getPanels = () => {
    let panels = [];
    children.map((child) => {
      const hasWrapperDiv =
        child.type !== AccordionTrigger && child.type !== AccordionContent;
      panels.push(hasWrapperDiv ? child.props.children : child);
      return panels;
    });
    panels = panels.flat();
    return panels;
  };

  const showNextTab = () => {
    const newFocusedIndex = focusedIndex + 1;
    if (newFocusedIndex < children.length) {
      setFocusedIndex(newFocusedIndex);
    }
  };

  const showPrevTab = () => {
    const newFocusedIndex = focusedIndex - 1;
    if (newFocusedIndex < children.length && newFocusedIndex >= 0) {
      setFocusedIndex(newFocusedIndex);
    }
  };

  const connectChildrenWithToggleHandling = () => {
    const childrenArray = [];
    const panels = getPanels();

    panels.map((child, index) => {
      const { props, type } = child;

      let newProps;
      let expandableButton;

      if (type === AccordionTrigger) {
        const isExpanded = isIndexExpanded(index);
        newProps = {
          isExpanded,
          focusedIndex,
          index,
          key: `child-${index}`,
          showNextTab,
          showPrevTab,
        };
      }

      if (type === AccordionContent) {
        const titleIndex = index - 1;
        const isExpanded = isIndexExpanded(titleIndex);
        newProps = {
          isExpanded,
          index: titleIndex,
          key: `child-${index}`,
        };

        if (closeAlsoOnContentClick) {
          newProps = {
            ...newProps,
            onClick: onTitleClick,
          };
        }
        const titlePropsForButton = {
          isExpanded,
          focusedIndex,
          index: titleIndex,
          onClick: onTitleClick,
          showNextTab,
          showPrevTab,
        };

        expandableButton = (
          <ExpandCollapseButtonWrapper
            isExpanded={isExpanded}
            // eslint-disable-next-line react/no-array-index-key
            key={`button-child-top-${index}`}
          >
            <StyledButton
              id={`accordion-trigger-${index}`}
              type="button"
              onKeyUp={(e) => {
                const isEnterOrSpaceBar = isSpaceBar(e) || isEnter(e);
                if (isEnterOrSpaceBar) {
                  onTitleClick(e, titlePropsForButton);
                }
              }}
              onKeyDown={onKeyDown}
              onClick={(event) => onTitleClick(event, titlePropsForButton)}
              data-testid={expandCollapseButtonTestId}
              data-aria-expanded={isExpanded}
              data-aria-label={
                !isExpanded ? expandButtonAriaLabel : collapseButtonAriaLabel
              }
              aria-controls={expandButtonAriaControls}
              dataTracking={expandButtonDataTracking}
            >
              {isExpanded ? collapseButtonLabel : expandButtonLabel}
              <IconWrapper
                isExpanded={isExpanded}
                useAs="decorative"
                icon={DoubleChevronDown}
                fillColor="currentColor"
              />
            </StyledButton>
          </ExpandCollapseButtonWrapper>
        );
      }

      childrenArray.push(expandableButton);

      childrenArray.push(React.createElement(type, { ...props, ...newProps }));

      return childrenArray;
    });

    return childrenArray;
  };

  const items = connectChildrenWithToggleHandling();
  return (
    <Container id={id} className={className} data-testid={dataTestid}>
      <AccordionTag className="accordion" role="presentation">
        {items}
      </AccordionTag>
    </Container>
  );
};

Accordion.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  dataTestid: PropTypes.string,
  children: PropTypes.node,
  activeIndex: PropTypes.number,
  focusedIndex: PropTypes.number,
  onTitleClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  expandButtonLabel: PropTypes.string,
  collapseButtonLabel: PropTypes.string,
  expandButtonAriaLabel: PropTypes.string,
  collapseButtonAriaLabel: PropTypes.string,
  closeAlsoOnContentClick: PropTypes.bool,
  expandCollapseButtonTestId: PropTypes.string,
  expandButtonAriaControls: PropTypes.string,
  expandButtonDataTracking: PropTypes.string,
};

Accordion.defaultProps = {
  children: "",
  activeIndex: -1,
  focusedIndex: -1,
  onTitleClick: () => {},
  onKeyDown: () => {},
  closeAlsoOnContentClick: false,
  expandButtonLabel: "Expand",
  collapseButtonLabel: "Collapse",
  expandCollapseButtonTestId: undefined,
  expandButtonAriaLabel: "",
  collapseButtonAriaLabel: "",
  expandButtonAriaControls: "",
  expandButtonDataTracking: "",
};

Accordion.Content = AccordionContent;
Accordion.Trigger = AccordionTrigger;

export default Accordion;
